<template>
  <div
    ref="container"
    role="radiogroup"
    :aria-labelledby="fieldContext?.inputId"
    :aria-describedby="ariaDescribedBy"
    :aria-invalid="fieldContext?.validationStatus.value === 'error'"
    :class="[
      `flex w-full items-stretch gap-1 rounded-xl border p-1 transition`,
      `outline-2 -outline-offset-1`,
      !hasSelection && `focus-within:outline`,
      fieldContext?.validationStatus.value === 'error'
        ? `bg-red-50 outline-red-500 dark:bg-red-950`
        : `bg-card outline-blue-500 dark:border-border dark:bg-card`,
      modes.length === 6 && `grid grid-cols-3`,
      modes.length === 8 && `grid grid-cols-4`,
    ]"
    @focus="onFocus"
    @blur="onBlur"
  >
    <label v-for="mode in modes" :key="mode.id" class="m-0 flex grow basis-0">
      <input
        ref="inputElements"
        v-model="modelValue"
        :name="fieldName"
        :value="mode.id"
        type="radio"
        :disabled="isDisabled"
        class="peer size-0 border-0 opacity-0"
      />
      <span
        :class="[
          `peer flex grow items-center justify-center rounded-lg border border-card bg-transparent px-2 py-1`,
          `outline-2 -outline-offset-1 outline-blue-500 peer-focus:z-10 peer-focus-visible:outline`,
          `text-pretty text-center transition-colors`,
          isDisabled
            ? `cursor-not-allowed opacity-50`
            : `hover:cursor-pointer hover:bg-background`,
          `peer-checked:border-gray-200 peer-checked:bg-input peer-checked:text-foreground`,
          `dark:bg-card dark:hover:bg-muted peer-checked:dark:border-border`,
          !modelValue || modelValue !== mode.id ? `text-muted-foreground` : ``,
          size === 'normal' && `text-sm`,
          size === 'small' && `text-xs`,
        ]"
      >
        {{ mode.label }}
      </span>
    </label>
  </div>
</template>

<script setup lang="ts" generic="T extends string | number">
import { computed, useId, useTemplateRef } from "vue";

import { injectFieldContext } from "./context";

export type SwitcherMode<T extends string | number = string> = {
  id: T;
  label: string;
};

const modelValue = defineModel<T>({ default: "" as T });

const props = withDefaults(
  defineProps<{
    modes: SwitcherMode<T>[];
    name?: string;
    size?: "normal" | "small";
    disabled?: boolean;
    onFocus?: (event: FocusEvent) => void;
    onBlur?: (event: FocusEvent) => void;
  }>(),
  {
    size: "normal",
  }
);

const container = useTemplateRef("container");
const inputElements = useTemplateRef("inputElements");
const defaultName = useId();
const fieldContext = injectFieldContext(null);

const fieldName = computed(() => props.name ?? defaultName);

const hasSelection = computed(() => {
  return props.modes.some((mode) => mode.id === modelValue.value);
});

const isDisabled = computed(() => {
  return fieldContext?.disabled.value || props.disabled;
});

const ariaDescribedBy = computed(() => {
  if (!fieldContext) {
    return undefined;
  }

  const ids: string[] = [
    fieldContext.descriptionId,
    fieldContext.hintId,
    fieldContext.validationMessageId,
  ];

  return ids.join(" ");
});

const focus = () => {
  const first = inputElements.value?.[0];
  if (first) {
    first.focus();
  }
};

defineExpose({
  input: computed(() => ({
    focus,
    el: container.value,
  })),
});
</script>
